// src/pages/Dashboard.js
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import AppNav from '../components/AppNav';
import { HomeScreenPrompt } from '../components/AddToHomeScreen';
import { fetchPrograms, fetchSiteContent, fetchUserCompletions, computeStats, generateSlug } from '../services/firebase';
import { fetchVideosFromCollection } from '../services/bunnyStream';
import { PushOptInCard } from '../components/PushNotificationPrompt';
import InAppNotification from '../components/InAppNotification';
import { getUnseenNotification, dismissNotification } from '../services/inAppNotifications';
import CoachPosts from '../components/CoachPosts';
import { hasActiveSubscription } from '../services/classAccess';
import { fetchNextUpcomingClass, fetchReleaseClassesForDay } from '../services/liveClasses';
import { isReleaseDayClass, isReleasedForViewer, localDateKey, formatReleaseDate } from '../services/classRelease';
import MOCK_PROGRAMS from '../data/mockPrograms';
import MilestoneShelf from '../components/MilestoneShelf';
import MilestoneCelebration from '../components/MilestoneCelebration';
import { computeMilestones, claimNewlyEarned } from '../services/milestones';
import CommunityCounter from '../components/CommunityCounter';
import { fetchCommunityTotal } from '../services/communityStats';
import { publishMilestones, readPublished, writePublished, displayNameFor } from '../services/milestoneWall';

function Dashboard({ demoMode }) {
    const navigate = useNavigate();
    const [programs, setPrograms] = useState([]);
    const [loading, setLoading] = useState(true);
    const [c, setC] = useState({});
    const [stats, setStats] = useState(null);
    const [completions, setCompletions] = useState([]);
    const [videoCounts, setVideoCounts] = useState({});
    const [assignedVideoIds, setAssignedVideoIds] = useState({});
    const [inAppNotification, setInAppNotification] = useState(null);
    const [nextClass, setNextClass] = useState(null);
    const [todayReleases, setTodayReleases] = useState([]);
    const [celebrating, setCelebrating] = useState([]);
    const [communityTotal, setCommunityTotal] = useState(null);

    let purchasedIds = [];
    let isAdmin = false;
    let isFriend = false;
    let userName = '';
    let userId = '';
    let clerkUser = null;
    if (!demoMode) {
        try {
            const { user } = useUser();
            purchasedIds = user?.publicMetadata?.purchasedPrograms || [];
            isAdmin = user?.publicMetadata?.role === 'admin';
            isFriend = user?.publicMetadata?.role === 'friend';
            userName = user?.firstName || '';
            userId = user?.id || '';
            clerkUser = user;
        } catch (e) { }
    } else {
        purchasedIds = ['mock-1'];
        isAdmin = true;
        userName = 'Danielle';
        userId = 'demo';
    }

    // Preview mode: admin can see app as a regular user would
    const previewAsUser = typeof window !== 'undefined' && localStorage.getItem('mom-preview-as-user') === 'true';
    if (previewAsUser && isAdmin) { isAdmin = false; }
    const hasFullAccess = isAdmin || isFriend;
    // "Member" = anyone who should see member-exclusive Dashboard moments
    // (the warm subtitle variant + the "Your next class" card). Mirrors the
    // gate used for CoachPosts so the three member-only touchpoints stay
    // consistent.
    const isMember = hasFullAccess || hasActiveSubscription(clerkUser);

    // ── Milestone badges ─────────────────────────────────────────────────
    // Derived from the completions we already fetched — ZERO extra reads.
    // Gated on isMember: badges are a membership moment, so a lapsed member
    // sees the Dashboard exactly as it was before this feature existed
    // (no dimmed trophy case on the way out).
    const milestones = useMemo(
        () => (isMember ? computeMilestones(completions) : null),
        [completions, isMember]
    );

    useEffect(() => {
        if (loading || !userId || !milestones) return;
        const fresh = claimNewlyEarned(userId, milestones.earned);
        if (fresh.length) setCelebrating(fresh);
    }, [loading, userId, milestones]);

    // ── Publish to the Milestone Wall ────────────────────────────────────
    // The member's own session is the only place their earned dates are known
    // (they're derived from their completions), so it does the publishing.
    // Doc ids are deterministic (`${userId}_${badgeKey}`) so this is idempotent;
    // the localStorage set keeps the steady state at ZERO writes.
    useEffect(() => {
        if (loading || !userId || !milestones || !isMember) return;
        if (!milestones.earned.length) return;
        // The Wall is for members. Read the role straight off Clerk rather than
        // using `isAdmin`, which "Preview as User" deliberately flips to false —
        // otherwise previewing would publish the coach onto the Wall.
        if (clerkUser?.publicMetadata?.role === 'admin') return;
        const already = readPublished(userId);
        const pending = milestones.earned.filter((b) => !already.has(b.key));
        if (pending.length === 0) return;
        let cancelled = false;
        publishMilestones(userId, displayNameFor(clerkUser), milestones.earned, already).then((done) => {
            if (cancelled || !done.length) return;
            done.forEach((k) => already.add(k));
            writePublished(userId, already);
        });
        return () => { cancelled = true; };
    }, [loading, userId, milestones, isMember, clerkUser]);

    // Community total — ONE doc read, and only for members (the only people
    // who see the counter).
    useEffect(() => {
        if (!isMember) return;
        let cancelled = false;
        fetchCommunityTotal().then((r) => {
            if (!cancelled && r) setCommunityTotal(r.total);
        });
        return () => { cancelled = true; };
    }, [isMember]);

    useEffect(() => {
        Promise.all([fetchPrograms(), fetchSiteContent(), fetchUserCompletions(userId)]).then(([p, content, userCompletions]) => {
            const progs = (p || MOCK_PROGRAMS)
                .sort((a, b) => (a.order || 999) - (b.order || 999));
            setPrograms(progs);
            setC(content || {});
            setCompletions(userCompletions || []);
            const activeProgs = progs.filter((prog) => prog.active !== false);
            const userPurchased = activeProgs.filter((pr) => purchasedIds.includes(pr.id));
            setStats(computeStats(userCompletions || [], userPurchased.length));
            setLoading(false);

            // Fetch actual video counts from Bunny for purchased programs
            // For admin/friend: count all visible programs; for regular users: only purchased
            const programsToCount = (hasFullAccess ? activeProgs : userPurchased).filter((pr) => pr.bunnyCollectionId);
            if (programsToCount.length > 0) {
                Promise.all(programsToCount.map((pr) =>
                    fetchVideosFromCollection(pr.bunnyCollectionId).then((videos) => ({
                        id: pr.id,
                        count: videos ? getAssignedVideoCount(pr, videos) : 0,
                        videoIds: videos ? getAssignedVideoIds(pr, videos) : [],
                    }))
                )).then((results) => {
                    const counts = {};
                    const idMap = {};
                    results.forEach((r) => { counts[r.id] = r.count; idMap[r.id] = new Set(r.videoIds); });
                    setVideoCounts(counts);
                    setAssignedVideoIds(idMap);
                });
            }
        });
    }, []);

    // Load unseen in-app notification (non-blocking, runs alongside main data)
    useEffect(() => {
        if (!userId || demoMode) return;
        // isMember = same gate as Coach Posts; members-only announcements are
        // skipped for everyone else (chosen during selection, see service).
        getUnseenNotification(userId, { isMember }).then((n) => {
            if (n) setInAppNotification(n);
        }).catch(() => { /* non-critical */ });
    }, [userId, demoMode, isMember]);

    // Members only: fetch the next upcoming class for the "Your next class"
    // card. Non-members fire ZERO new Firestore reads — the gate happens
    // before the network call. Failure is silent (card just doesn't render),
    // so an index hiccup or permissions blip can't surface a broken UI.
    useEffect(() => {
        if (!isMember) return;
        // Pass identity so the card respects the class visibility allowlist —
        // a class gated to specific users must not surface to others here.
        fetchNextUpcomingClass({ userId, isAdmin })
            .then((cls) => { if (cls) setNextClass(cls); })
            .catch(() => { /* non-critical — card stays hidden */ });
        // Today's release-day classes (services/classRelease.js) — kept on the card
        // all day until she's done one, since they leave fetchNextUpcomingClass at
        // ~7 AM Central. One small equality read; members only, like the card.
        fetchReleaseClassesForDay(localDateKey(new Date()), { userId, isAdmin })
            .then((list) => setTodayReleases(list.filter((c) => isReleasedForViewer(c))))
            .catch(() => { /* non-critical */ });
    }, [isMember, userId, isAdmin]);

    const handleDismissNotification = (notificationId) => {
        setInAppNotification(null);
        if (userId) dismissNotification(userId, notificationId).catch(() => {});
    };

    // Card priority: a class live right now → today's release she hasn't done yet
    // → the next upcoming class. With no release-day classes this is nextClass,
    // exactly as before.
    const todayKeyLocal = localDateKey(new Date());
    const doneToday = (cls) => completions.some((x) => x.videoId === cls.muxAssetId
        && x.completedAt && localDateKey(new Date(x.completedAt)) === todayKeyLocal);
    const todayRelease = todayReleases.find((c) => c.muxAssetId && !doneToday(c)) || null;
    const nextIsLive = !!nextClass && !isReleaseDayClass(nextClass) && ['live', 'ending'].includes(nextClass.state || nextClass.status);
    // A release she's already done today isn't her "next" class — don't offer it.
    const nextDoneRelease = !!nextClass && isReleaseDayClass(nextClass) && isReleasedForViewer(nextClass) && doneToday(nextClass);
    const cardClass = nextIsLive ? nextClass : (todayRelease || (nextDoneRelease ? null : nextClass));

    const visiblePrograms = programs.filter((prog) => isAdmin || prog.active !== false);
    const purchased = hasFullAccess ? visiblePrograms : visiblePrograms.filter((p) => purchasedIds.includes(p.id));
    const hasPurchased = purchased.length > 0;

    // Build per-program completion counts — only count videos currently assigned to the program
    const programCompletionMap = {};
    const seenPerProgram = {};
    completions.forEach((comp) => {
        if (comp.programId && comp.videoId) {
            // If we have the assigned video IDs for this program, only count if the video is still assigned
            const validIds = assignedVideoIds[comp.programId];
            if (validIds && !validIds.has(comp.videoId)) return;
            if (!seenPerProgram[comp.programId]) seenPerProgram[comp.programId] = new Set();
            seenPerProgram[comp.programId].add(comp.videoId);
        }
    });
    Object.keys(seenPerProgram).forEach((pid) => {
        programCompletionMap[pid] = seenPerProgram[pid].size;
    });

    return (
        <div className="mom-page-shell" style={{ background: 'var(--mom-cream)', minHeight: '100vh', position: 'relative', zIndex: 1 }}>
            <AppNav demoMode={demoMode} />
            {!demoMode && <HomeScreenPrompt />}
            {celebrating.length > 0 && (
                <MilestoneCelebration badges={celebrating} copy={c} onClose={() => setCelebrating([])} />
            )}
            <div style={{ maxWidth: 1100, margin: '0 auto', padding: '32px 24px' }}>

                {loading ? (
                    /* Skeleton loading */
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                        <div className="mom-skeleton" style={{ height: 200, borderRadius: 28 }} />
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 }}>
                            <div className="mom-skeleton" style={{ height: 260, borderRadius: 22 }} />
                            <div className="mom-skeleton" style={{ height: 260, borderRadius: 22 }} />
                        </div>
                    </div>
                ) : (
                    <>
                        {/* ── HERO: YOUR WEEK ─────────────────────── */}
                        <div className="slide-up" style={{ position: 'relative', marginBottom: 28 }}>
                            {/* Glow behind */}
                            <div style={{
                                position: 'absolute', top: '-12%', left: '8%', right: '8%', bottom: '-8%',
                                background: 'radial-gradient(ellipse 90% 60% at 50% 40%, color-mix(in srgb, var(--mom-primary) 14%, transparent) 0%, transparent 70%)',
                                pointerEvents: 'none', zIndex: 0,
                            }} />
                            <div style={{
                                position: 'relative', zIndex: 1,
                                background: 'rgba(255,255,255,0.55)', backdropFilter: 'blur(20px) saturate(1.4)', WebkitBackdropFilter: 'blur(20px) saturate(1.4)',
                                borderRadius: 28,
                                padding: 'clamp(32px, 5vw, 44px) clamp(24px, 4vw, 40px)',
                                overflow: 'hidden',
                                boxShadow: 'var(--shadow-lg)',
                                border: '1px solid rgba(255,255,255,0.55)',
                            }}>
                                {/* Specular highlight */}
                                <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '50%', background: 'radial-gradient(ellipse 70% 80% at 25% 0%, rgba(255,255,255,0.28) 0%, transparent 60%)', pointerEvents: 'none', zIndex: 0, borderRadius: '28px 28px 0 0' }} />

                                <div style={{ position: 'relative', zIndex: 1 }}>
                                    {/* Greeting */}
                                    <p style={{
                                        fontFamily: 'var(--mom-font-serif)', fontSize: 'clamp(24px, 4vw, 32px)',
                                        fontStyle: 'italic', fontWeight: 700, color: 'var(--mom-charcoal)',
                                        lineHeight: 1.2, marginBottom: 4,
                                    }}>
                                        {getGreeting()}{userName ? `, ${userName}` : ''}
                                    </p>
                                    <p style={{
                                        fontFamily: 'var(--mom-font-sans)', fontSize: 14,
                                        color: 'var(--mom-warm-gray)', marginBottom: 28, lineHeight: 1.5,
                                    }}>
                                        {/* Members see a warmer acknowledgment of their membership.
                                            Falls through to the universal subtitle if the admin-edited
                                            member field is cleared, then to the baked-in default. */}
                                        {isMember
                                            ? (c.dashboardMemberSubtitle || c.dashboardSubtitle || "Your membership is active — here's how your week is shaping up.")
                                            : (c.dashboardSubtitle || "Here's how your week is shaping up.")}
                                    </p>

                                    {/* Progress Arc + Stats */}
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 'clamp(24px, 4vw, 40px)', flexWrap: 'wrap' }}>
                                        {/* Arc visualization */}
                                        <div style={{ position: 'relative', width: 130, height: 80, flexShrink: 0, margin: '0 auto' }}>
                                            <WeeklyArc count={stats?.thisWeek || 0} goal={3} />
                                            <div style={{
                                                position: 'absolute', bottom: 2, left: '50%', transform: 'translateX(-50%)',
                                                textAlign: 'center',
                                            }}>
                                                <span style={{
                                                    fontFamily: 'var(--mom-font-serif)', fontSize: 32,
                                                    fontWeight: 700, fontStyle: 'italic', lineHeight: 1,
                                                    color: 'var(--mom-primary)',
                                                }}>{stats?.thisWeek || 0}</span>
                                                <p style={{
                                                    fontFamily: 'var(--mom-font-sans)', fontSize: 10, fontWeight: 600,
                                                    color: 'var(--mom-warm-gray)', letterSpacing: '0.08em',
                                                    textTransform: 'uppercase', marginTop: 2,
                                                }}>this week</p>
                                            </div>
                                        </div>

                                        {/* Motivation + details */}
                                        <div style={{ flex: 1, minWidth: 160 }}>
                                            <p style={{
                                                fontFamily: 'var(--mom-font-serif)', fontSize: 17,
                                                fontStyle: 'italic', fontWeight: 600,
                                                color: 'var(--mom-charcoal)', lineHeight: 1.4, marginBottom: 12,
                                            }}>
                                                {getMotivation(stats?.thisWeek || 0, stats?.streak || 0, c)}
                                            </p>

                                            {/* Streak badge */}
                                            {stats && stats.streak > 0 && (
                                                <div style={{
                                                    display: 'inline-flex', alignItems: 'center', gap: 6,
                                                    padding: '6px 14px', borderRadius: 20,
                                                    background: 'linear-gradient(135deg, color-mix(in srgb, var(--mom-primary) 10%, transparent), color-mix(in srgb, var(--mom-primary) 5%, transparent))',
                                                    marginBottom: 12,
                                                }}>
                                                    <span style={{ fontSize: 14 }}>🔥</span>
                                                    <span style={{
                                                        fontFamily: 'var(--mom-font-sans)', fontSize: 12, fontWeight: 700,
                                                        color: 'var(--mom-primary-dark)', letterSpacing: '0.02em',
                                                    }}>{stats.streak} day streak</span>
                                                </div>
                                            )}

                                            {/* Streak held-by-grace nudge: alive on yesterday, not moved yet today */}
                                            {stats && stats.streak > 0 && !stats.todayActive && (
                                                <p style={{
                                                    fontFamily: 'var(--mom-font-sans)', fontSize: 12, fontWeight: 600,
                                                    color: 'var(--mom-primary-dark)', marginBottom: 12, marginTop: -4,
                                                }}>
                                                    {c.streakHeldHint || 'Move today to keep your streak alive!'}
                                                </p>
                                            )}

                                            {/* Quiet stat line */}
                                            {stats && stats.total > 0 && (
                                                <p style={{
                                                    fontFamily: 'var(--mom-font-sans)', fontSize: 12,
                                                    color: 'var(--mom-warm-gray)',
                                                }}>
                                                    {stats.total} total workout{stats.total !== 1 ? 's' : ''} completed{stats.programs > 0 ? ` · ${stats.programs} program${stats.programs !== 1 ? 's' : ''}` : ''}
                                                </p>
                                            )}

                                            {/* Monthly challenge stats — admin-toggled for a challenge month */}
                                            {stats && c.showMonthlyChallengeStats && (
                                                <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
                                                    {[
                                                        (c.challengeActiveDaysLabel || '{count} active days this month').replace('{count}', stats.activeDaysThisMonth || 0),
                                                        (c.challengeSessionsLabel || '{count} workouts this month').replace('{count}', stats.totalSessionsThisMonth || 0),
                                                    ].map((txt, idx) => (
                                                        <span key={idx} style={{
                                                            fontFamily: 'var(--mom-font-sans)', fontSize: 11, fontWeight: 600,
                                                            color: 'var(--mom-primary-dark)', background: 'color-mix(in srgb, var(--mom-primary) 8%, transparent)',
                                                            padding: '5px 12px', borderRadius: 20,
                                                        }}>{txt}</span>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* ══════════════════════════════════════════════ */}
                        {/*  Member-only: milestone badge shelf. Purely       */}
                        {/*  additive — renders nothing for non-members, and  */}
                        {/*  nothing for a member with no completions yet.    */}
                        {/* ══════════════════════════════════════════════ */}
                        {isMember && milestones && (
                            <MilestoneShelf
                                milestones={milestones}
                                copy={c}
                                onViewAll={() => navigate('/milestones')}
                            />
                        )}

                        {/* ══════════════════════════════════════════════ */}
                        {/*  Member-only: "Your next class" card.            */}
                        {/*  Hidden entirely for non-members and when no     */}
                        {/*  upcoming class exists. Sits between the hero    */}
                        {/*  and the universal Workouts CTA so the member    */}
                        {/*  signal lands above the universal one.           */}
                        {/* ══════════════════════════════════════════════ */}
                        {isMember && cardClass && (
                            <NextClassCard cls={cardClass} copy={c} onClick={() => navigate('/workouts')} />
                        )}

                        {/* ══════════════════════════════════════════════ */}
                        {/*  Non-member acquisition CTA. Members are served  */}
                        {/*  by the NextClassCard above (clean cut — when no */}
                        {/*  upcoming class exists, members have no Dashboard */}
                        {/*  CTA to /workouts and rely on the nav). Routes to */}
                        {/*  /workouts where Subscribe CTAs do conversion.    */}
                        {/* ══════════════════════════════════════════════ */}
                        {!isMember && (
                            <div className="slide-up stagger-1" onClick={() => navigate('/workouts')}
                                style={{
                                    background: 'linear-gradient(135deg, var(--mom-primary), var(--mom-primary-dark))',
                                    borderRadius: 20, padding: '20px 24px',
                                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                    cursor: 'pointer', marginBottom: 28,
                                    boxShadow: '0 4px 16px color-mix(in srgb, var(--mom-primary) 25%, transparent), var(--shadow-sm)',
                                    border: '1px solid color-mix(in srgb, var(--mom-primary) 30%, transparent)',
                                    transition: 'all 0.2s ease',
                                    position: 'relative',
                                    overflow: 'hidden',
                                }}>
                                {/* Specular highlight — matches hero card treatment */}
                                <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '40%', background: 'linear-gradient(180deg, rgba(255,255,255,0.08) 0%, transparent 100%)', pointerEvents: 'none', borderRadius: '20px 20px 0 0' }} />
                                <div style={{ position: 'relative', zIndex: 1 }}>
                                    <p style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 16, fontStyle: 'italic', fontWeight: 600, color: 'var(--mom-white)', marginBottom: 2 }}>{c.dashboardQuickLinkTitle || 'Browse upcoming classes'}</p>
                                    <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, color: 'rgba(255,255,255,0.8)' }}>{c.dashboardQuickLinkSubtitle || 'See what\'s coming up — live, on-demand, and more'}</p>
                                </div>
                                <span style={{ color: 'var(--mom-white)', fontSize: 18, fontWeight: 600, position: 'relative', zIndex: 1 }}>→</span>
                            </div>
                        )}

                        {/* ── Push notification opt-in ─────────────── */}
                        <PushOptInCard userId={userId} />

                        {/* ── Community counter (members only) ──────────────
                            One shared number instead of community badges, so it
                            reads as a collective achievement, not a leaderboard.
                            Renders nothing until the aggregate doc loads. */}
                        {isMember && <CommunityCounter total={communityTotal} copy={c} />}

                        {/* ── Coach posts (subscribers + admin/friend only) ── */}
                        {(hasFullAccess || hasActiveSubscription(clerkUser)) && (
                            <CoachPosts coachAvatarUrl={c.coachAvatarUrl} />
                        )}

                        {/* ══════════════════════════════════════════════ */}
                        {/*  PURCHASED USER — Program Cards                 */}
                        {/* ══════════════════════════════════════════════ */}
                        {hasPurchased && (
                            <section style={{ marginBottom: 28 }}>
                                <h2 style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 22, fontStyle: 'italic', color: 'var(--mom-charcoal)', marginBottom: 18 }}>
                                    {c.dashboardPurchasedHeading || (purchased.some((p) => (programCompletionMap[p.id] || 0) > 0) ? 'Continue Your Programs' : 'Your Programs')}
                                </h2>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 }}>
                                    {purchased.map((prog, i) => (
                                        <PurchasedProgramCard key={prog.id} program={prog} delay={i * 0.1} onClick={() => navigate(`/program/${generateSlug(prog.name)}`)}
                                            completedCount={programCompletionMap[prog.id] || 0}
                                            totalCount={videoCounts[prog.id] !== undefined ? videoCounts[prog.id] : null} />
                                    ))}
                                </div>
                            </section>
                        )}

                        {/* ══════════════════════════════════════════════ */}
                        {/*  NO PROGRAMS — Soft secondary link to Programs  */}
                        {/* ══════════════════════════════════════════════ */}
                        {!hasPurchased && (
                            <div style={{ textAlign: 'center', marginTop: 20, marginBottom: 48 }}>
                                <button className="mom-btn mom-btn-ghost" onClick={() => navigate('/programs')}
                                    style={{ fontSize: 14, color: 'var(--mom-link)', fontWeight: 500 }}>
                                    {c.dashboardBrowseLabel || 'Or browse structured programs →'}
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* In-app notification popup — shown once per unseen announcement */}
            {inAppNotification && (
                <InAppNotification notification={inAppNotification} onDismiss={handleDismissNotification} />
            )}
        </div>
    );
}

// ── Time-aware greeting ──────────────────────────────────────
function getGreeting() {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 17) return 'Good afternoon';
    return 'Good evening';
}

// ── Dynamic motivational copy ────────────────────────────────
function getMotivation(thisWeek, streak, c) {
    if (thisWeek === 0 && streak === 0) return c?.motivation0 || "Ready to move? Even a few minutes makes a difference.";
    if (thisWeek === 0 && streak > 0) return c?.motivation0streak || "Let's keep that streak alive — you've got this!";
    if (thisWeek === 1) return c?.motivation1 || "You showed up — that's what matters. Two more this week?";
    if (thisWeek === 2) return c?.motivation2 || "Almost there! One more and you've hit your goal.";
    if (thisWeek === 3) return c?.motivation3 || "You hit your goal! Three this week — so proud of you.";
    if (thisWeek === 4) return c?.motivation4 || "Going above and beyond — four this week!";
    if (thisWeek >= 5) return c?.motivation5plus || "Incredible week! You're really showing up for yourself.";
    return "Keep going — every movement counts.";
}

// ── Weekly Progress Arc (SVG semicircle) ─────────────────────
function WeeklyArc({ count, goal }) {
    const size = 130;
    const strokeWidth = 8;
    const radius = (size - strokeWidth) / 2;
    // Semicircle: arc from 180° to 0° (bottom-left to bottom-right over the top)
    const circumference = Math.PI * radius; // half circle
    const pct = Math.min(count / goal, 1);
    const offset = circumference - pct * circumference;

    return (
        <svg width={size} height={size / 2 + strokeWidth} viewBox={`0 0 ${size} ${size / 2 + strokeWidth}`} style={{ display: 'block', margin: '0 auto', overflow: 'visible' }}>
            {/* Track */}
            <path
                d={`M ${strokeWidth / 2} ${size / 2} A ${radius} ${radius} 0 0 1 ${size - strokeWidth / 2} ${size / 2}`}
                fill="none"
                stroke="color-mix(in srgb, var(--mom-primary) 8%, transparent)"
                strokeWidth={strokeWidth}
                strokeLinecap="round"
            />
            {/* Fill */}
            {pct > 0 && (
                <path
                    d={`M ${strokeWidth / 2} ${size / 2} A ${radius} ${radius} 0 0 1 ${size - strokeWidth / 2} ${size / 2}`}
                    fill="none"
                    stroke="var(--mom-primary)"
                    strokeWidth={strokeWidth}
                    strokeLinecap="round"
                    strokeDasharray={circumference}
                    strokeDashoffset={offset}
                    style={{ transition: 'stroke-dashoffset 1.2s cubic-bezier(0.4, 0, 0.2, 1)' }}
                />
            )}
        </svg>
    );
}

// ── Helper: count assigned videos that actually exist in Bunny ──
function getAssignedVideoCount(program, bunnyVideos) {
    return getAssignedVideoIds(program, bunnyVideos).length;
}

function getAssignedVideoIds(program, bunnyVideos) {
    const existingIds = new Set(bunnyVideos.map((v) => v.id));
    if (program.programType === 'cohort' && program.weeks && program.weeks.length > 0) {
        const assignedIds = new Set();
        program.weeks.forEach((week) => {
            (week.videoIds || []).forEach((id) => {
                if (existingIds.has(id)) assignedIds.add(id);
            });
        });
        return [...assignedIds];
    }
    if (program.videoOrder && program.videoOrder.length > 0) {
        return program.videoOrder.filter((id) => existingIds.has(id));
    }
    return bunnyVideos.map((v) => v.id);
}

// ── Purchased Program Card ──────────────────────────────────
function PurchasedProgramCard({ program, delay, onClick, completedCount, totalCount }) {
    const [hovered, setHovered] = useState(false);
    const hasImage = program.imageUrl;
    const hasProgress = totalCount > 0;
    const progressPct = hasProgress ? Math.min(Math.round((completedCount / totalCount) * 100), 100) : 0;
    const allDone = hasProgress && completedCount >= totalCount;

    return (
        <div style={{ position: 'relative' }}>
            {/* Glow behind card */}
            <div style={{
                position: 'absolute', top: '-10%', left: '8%', right: '8%', bottom: '-8%',
                background: 'radial-gradient(ellipse 90% 60% at 40% 35%, color-mix(in srgb, var(--mom-primary) 12%, transparent) 0%, transparent 70%)',
                pointerEvents: 'none', zIndex: 0,
                opacity: hovered ? 1 : 0.5,
                transition: 'opacity 0.3s ease',
            }} />
            <div className="slide-up" onClick={onClick} onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
                style={{
                    position: 'relative', zIndex: 1,
                    background: 'rgba(255,255,255,0.55)', backdropFilter: 'blur(20px) saturate(1.4)', WebkitBackdropFilter: 'blur(20px) saturate(1.4)',
                    borderRadius: 22,
                    overflow: 'hidden',
                    cursor: 'pointer',
                    boxShadow: hovered ? 'var(--shadow-lg)' : 'var(--shadow-md)',
                    transform: hovered ? 'translateY(-4px)' : 'none',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    animationDelay: `${delay}s`,
                    border: '1px solid rgba(255,255,255,0.55)',
                }}>
                {/* Specular highlight */}
                <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '50%', background: 'radial-gradient(ellipse 70% 80% at 25% 0%, rgba(255,255,255,0.28) 0%, transparent 60%)', pointerEvents: 'none', zIndex: 1, borderRadius: '22px 22px 0 0' }} />
                <div style={{
                    height: hasImage ? 170 : 110,
                    background: 'linear-gradient(135deg, var(--mom-charcoal), var(--mom-charcoal-light))',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                    position: 'relative',
                }}>
                    {hasImage ? (
                        <img src={program.imageUrl} alt={program.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} referrerPolicy="no-referrer" />
                    ) : null}
                    {hasImage && <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, transparent 40%, rgba(0,0,0,0.15) 100%)' }} />}
                    <div style={{
                        position: 'absolute', top: 12, right: 12,
                        background: allDone ? 'linear-gradient(135deg, var(--mom-success), color-mix(in srgb, var(--mom-success) 80%, #000))' : 'linear-gradient(135deg, var(--mom-primary), var(--mom-primary-dark))',
                        color: 'var(--mom-white)',
                        fontFamily: 'var(--mom-font-sans)',
                        fontSize: 10,
                        fontWeight: 700,
                        padding: '5px 14px',
                        borderRadius: 20,
                        letterSpacing: '0.04em',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    }}>{allDone ? 'COMPLETE' : 'ENROLLED'}</div>
                </div>
                <div style={{ padding: '20px 24px', position: 'relative', zIndex: 2 }}>
                    <h3 style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 18, marginBottom: 8, color: 'var(--mom-charcoal)' }}>{program.name}</h3>

                    {hasProgress && (
                        <div style={{ marginBottom: 12 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                                <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, color: allDone ? 'var(--mom-success)' : 'var(--mom-warm-gray)', fontWeight: 600 }}>
                                    {allDone ? '✓ All workouts completed' : `${completedCount} of ${totalCount} completed`}
                                </span>
                                <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 11, color: 'var(--mom-warm-gray)' }}>{progressPct}%</span>
                            </div>
                            <div className="mom-inset" style={{ height: 6, borderRadius: 3, overflow: 'hidden', padding: 0, boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.06)' }}>
                                <div style={{
                                    height: '100%',
                                    borderRadius: 3,
                                    background: allDone ? 'linear-gradient(90deg, var(--mom-success), color-mix(in srgb, var(--mom-success) 80%, #fff))' : 'linear-gradient(90deg, var(--mom-primary), var(--mom-primary-light))',
                                    width: `${progressPct}%`,
                                    transition: 'width 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                                }} />
                            </div>
                        </div>
                    )}

                    <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, color: 'var(--mom-link)', fontWeight: 600 }}>
                        {allDone ? 'Review program →' : 'Continue program →'}
                    </p>
                </div>
            </div>
        </div>
    );
}

// ── Time formatting for the "Your next class" card ──────────
// "On now" if live, else "Today · 9:00 AM" / "Tomorrow · 9:00 AM" /
// "Tuesday · 9:00 AM" within 7 days, else "Mon, May 24 · 9:00 AM".
function formatNextClassTime(iso, state) {
    if (!iso) return '';
    const d = new Date(iso);
    const now = new Date();
    const timeStr = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
    if (state === 'live' || state === 'ending') return 'On now — tap to watch';

    const sameDay = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
    if (sameDay(d, now)) return `Today · ${timeStr}`;
    const tomorrow = new Date(now); tomorrow.setDate(now.getDate() + 1);
    if (sameDay(d, tomorrow)) return `Tomorrow · ${timeStr}`;

    const daysOut = Math.floor((d - now) / (1000 * 60 * 60 * 24));
    if (daysOut < 7) return `${d.toLocaleDateString('en-US', { weekday: 'long' })} · ${timeStr}`;
    return `${d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })} · ${timeStr}`;
}

// ── Next-class member card (Dashboard, members + admin/friend only) ──
// Slim glass strip matching the Dashboard's existing aesthetic — lighter
// than the hero, lighter than the gradient CTA, so it reads as service
// rather than competing for primary action. Whole-card click routes to
// /workouts (the live tab is where the user can join, set a reminder, or
// open the class detail modal). Hover state via React useState — never
// inline DOM mutation (per project convention; mutating e.currentTarget
// leaks across renders).
function NextClassCard({ cls, onClick, copy = {} }) {
    const [hovered, setHovered] = useState(false);
    if (!cls) return null;
    const state = cls.state || cls.status;
    // A release-day class never airs — never "Live now", whatever its stored state.
    const isLive = (state === 'live' || state === 'ending') && !isReleaseDayClass(cls);
    const thumb = cls.thumbnailUrl || cls.imageUrl;
    // Release-day class (services/classRelease.js): no time — it's out for her
    // from 12:00 AM on its date. Every other class keeps formatNextClassTime.
    const isRelease = isReleaseDayClass(cls);
    const releasedNow = isRelease && isReleasedForViewer(cls);
    const eyebrow = isLive ? 'Live now' : releasedNow ? (copy.releaseTodayEyebrow || "Today's class") : 'Your next class';
    const whenLine = !isRelease ? formatNextClassTime(cls.scheduledAt, state)
        : releasedNow ? (copy.releaseDashOutNow || 'Out now — tap to start')
        : (() => {
            const t = new Date(); const tm = new Date(t.getFullYear(), t.getMonth(), t.getDate() + 1);
            const day = cls.releaseDate === localDateKey(tm) ? 'Tomorrow' : formatReleaseDate(cls.releaseDate, { weekday: 'long' });
            return `${day} · ${copy.releaseDashReadyFirstThing || 'ready first thing'}`;
        })();

    return (
        <div className="slide-up stagger-1" onClick={onClick}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            style={{
                position: 'relative',
                background: 'rgba(255,255,255,0.55)',
                backdropFilter: 'blur(20px) saturate(1.4)',
                WebkitBackdropFilter: 'blur(20px) saturate(1.4)',
                borderRadius: 20,
                padding: '14px 18px',
                marginBottom: 16,
                cursor: 'pointer',
                boxShadow: hovered ? 'var(--shadow-md)' : 'var(--shadow-sm)',
                transform: hovered ? 'translateY(-2px)' : 'none',
                transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                border: '1px solid rgba(255,255,255,0.55)',
                overflow: 'hidden',
                display: 'flex',
                alignItems: 'center',
                gap: 14,
            }}>
            {/* Specular highlight — matches glass cards elsewhere */}
            <div style={{
                position: 'absolute', top: 0, left: 0, right: 0, height: '50%',
                background: 'radial-gradient(ellipse 70% 80% at 25% 0%, rgba(255,255,255,0.28) 0%, transparent 60%)',
                pointerEvents: 'none', zIndex: 0, borderRadius: '20px 20px 0 0',
            }} />

            {/* Thumbnail (or brand-gradient fallback when no image) */}
            <div style={{
                position: 'relative', zIndex: 1,
                width: 56, height: 56, borderRadius: 12,
                overflow: 'hidden', flexShrink: 0,
                background: 'linear-gradient(135deg, var(--mom-primary), var(--mom-primary-dark))',
                boxShadow: '0 2px 8px color-mix(in srgb, var(--mom-primary) 22%, transparent)',
            }}>
                {thumb ? (
                    <img src={thumb} alt="" referrerPolicy="no-referrer"
                        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                ) : (
                    <div style={{
                        width: '100%', height: '100%',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontFamily: 'var(--mom-font-serif)', fontStyle: 'italic', fontWeight: 700,
                        color: '#fff', fontSize: 22, opacity: 0.85,
                    }}>m</div>
                )}
            </div>

            {/* Text column */}
            <div style={{ flex: 1, minWidth: 0, position: 'relative', zIndex: 1 }}>
                {/* Eyebrow with status dot — pink for upcoming, red for live */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                    <span style={{
                        width: 6, height: 6, borderRadius: '50%',
                        background: isLive ? '#dc2626' : 'var(--mom-primary)',
                        boxShadow: isLive ? '0 0 6px rgba(220,38,38,0.55)' : 'none',
                        flexShrink: 0,
                    }} />
                    <span style={{
                        fontFamily: 'var(--mom-font-sans)', fontSize: 10, fontWeight: 700,
                        letterSpacing: '0.1em', textTransform: 'uppercase',
                        color: isLive ? '#dc2626' : 'var(--mom-warm-gray)',
                    }}>{eyebrow}</span>
                </div>
                {/* Title */}
                <p style={{
                    fontFamily: 'var(--mom-font-serif)', fontSize: 16,
                    fontStyle: 'italic', fontWeight: 600,
                    color: 'var(--mom-charcoal)', lineHeight: 1.25,
                    marginBottom: 2,
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                }}>{cls.title || 'Live class'}</p>
                {/* Time */}
                <p style={{
                    fontFamily: 'var(--mom-font-sans)', fontSize: 12,
                    color: 'var(--mom-warm-gray)', lineHeight: 1.3,
                }}>{whenLine}</p>
            </div>

            {/* Arrow */}
            <span style={{
                position: 'relative', zIndex: 1,
                color: 'var(--mom-primary)', fontSize: 18, fontWeight: 600,
                transform: hovered ? 'translateX(2px)' : 'none',
                transition: 'transform 0.25s ease',
                flexShrink: 0,
            }}>→</span>
        </div>
    );
}

export default Dashboard;